/**
 * StaffingComments — geteilter Staffing-Kommentarblock (Staffing neu, Issue #1).
 * Zeigt die letzten Kommentare (Prop `limit`; Liste: 2, Projektdetail-Tab: alle) +
 * MentionInput zum Erfassen. Erfasser + Zeitstempel je Kommentar, @-Mentions gerendert.
 * Liest/schreibt über die bestehende Staffing-Comment-API.
 * #162: Eigene Kommentare bearbeitet der Autor hier (Stift → Editor), löschen darf er sie
 * (Admin auch fremde) mit Rückfrage. Beides nur, wo kommentiert werden darf (`canComment`).
 */
import type { StaffingComment } from '@projektverwaltung/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { entwurfSchluessel } from '../../lib/kommentar-entwurf.js';
import { useSession } from '../../lib/session.js';
import { MentionInput, renderMentionBody } from '../mention-input.js';
import { useToast } from '../toast.js';
import { useConfirm } from './confirm-dialog.js';
import { HerkunftBadge } from './herkunft-badge.js';

type NutzerMap = Map<string, { user_id: string; name: string; email: string }>;

export function StaffingComments({
  projectId,
  limit,
  canComment,
  onSaved,
}: {
  projectId: string;
  limit?: number;
  canComment: boolean;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);

  const { data } = useQuery({
    queryKey: ['v2-staffing-comments', projectId],
    // Staffing-Kontext: nur Staffing-Kommentare laden.
    queryFn: () => api.listStaffingComments(projectId, { staffingOnly: true }),
  });
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const usersById = useMemo(() => {
    const map: NutzerMap = new Map();
    for (const u of usersQuery.data?.items ?? []) map.set(u.user_id, u);
    return map;
  }, [usersQuery.data]);

  const neuLaden = () => {
    void qc.invalidateQueries({ queryKey: ['v2-staffing-comments', projectId] });
    void qc.invalidateQueries({ queryKey: ['v2-kl-staffing'] });
    // Kommentare-Reiter im Projektdetail zeigt dieselben Einträge.
    void qc.invalidateQueries({ queryKey: ['v2-comments', projectId] });
    // Aufrufer-eigene Sichten (z. B. Zeilenvorschau der KFML-Worklist) — auch nach Bearbeiten/Löschen.
    onSaved?.();
  };

  const create = useMutation({
    // Staffing-Kontext: neuer Kommentar ist immer ein Staffing-Kommentar.
    mutationFn: () =>
      api.createStaffingComment(projectId, { body: draft, mentions, is_staffing: true }),
    onSuccess: () => {
      setDraft('');
      setMentions([]);
      neuLaden();
    },
    onError: (err) =>
      toast.error(
        t('v2.staffing.comment_failed', 'Kommentar fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  // GET liefert bereits desc (neueste zuerst) → slice auf limit.
  const items = (data?.items ?? []).slice(0, limit ?? Number.POSITIVE_INFINITY);

  return (
    <div className="flex flex-col gap-2">
      {items.length === 0 && (
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.staffing.no_comments', 'Noch keine Staffing-Kommentare')}
        </p>
      )}
      {items.map((cm) => (
        <KommentarEintrag
          key={cm.staffing_comment_id}
          cm={cm}
          usersById={usersById}
          canComment={canComment}
          // Ohne Namensliste trügen die Chips im Editor nur UUIDs → Stift erst danach.
          namenGeladen={usersQuery.isSuccess}
          onGespeichert={(row) => {
            // Sofort den neuen Stand zeigen, statt kurz den alten bis zum Neuladen.
            qc.setQueryData<{ items: StaffingComment[] }>(
              ['v2-staffing-comments', projectId],
              (alt) =>
                alt && {
                  items: alt.items.map((x) =>
                    x.staffing_comment_id === row.staffing_comment_id ? { ...x, ...row } : x,
                  ),
                },
            );
            neuLaden();
          }}
          onGeaendert={neuLaden}
        />
      ))}
      {canComment && (
        <div className="flex flex-col gap-1">
          <MentionInput
            value={draft}
            entwurfSchluessel={entwurfSchluessel('staffing', projectId)}
            onChange={(text: string, ids: string[]) => {
              setDraft(text);
              setMentions(ids);
            }}
            placeholder={t('v2.staffing.comment_placeholder', 'Kommentar… (@ für Erwähnung)')}
            rows={2}
          />
          <button
            type="button"
            disabled={!draft.trim() || create.isPending}
            onClick={() => create.mutate()}
            className="self-end text-xs px-3 py-1 rounded"
            style={{
              background: 'var(--primary)',
              color: 'var(--on-primary)',
              border: 'none',
              opacity: draft.trim() && !create.isPending ? 1 : 0.5,
              cursor: draft.trim() && !create.isPending ? 'pointer' : 'not-allowed',
            }}
          >
            {t('v2.staffing.comment_submit', 'Kommentieren')}
          </button>
        </div>
      )}
    </div>
  );
}

function KommentarEintrag({
  cm,
  usersById,
  canComment,
  namenGeladen,
  onGespeichert,
  onGeaendert,
}: {
  cm: StaffingComment;
  usersById: NutzerMap;
  canComment: boolean;
  namenGeladen: boolean;
  onGespeichert: (row: StaffingComment) => void;
  onGeaendert: () => void;
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const bestaetigen = useConfirm();
  const { user } = useSession();
  const [bearbeiten, setBearbeiten] = useState(false);
  const [text, setText] = useState('');

  const author = cm.created_by ? usersById.get(cm.created_by) : undefined;
  const eigener = !!user && cm.created_by === user.user_id;
  const darfBearbeiten = canComment && eigener && namenGeladen;
  const darfLoeschen = canComment && (eigener || !!user?.is_admin);
  const zeit = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'short', timeStyle: 'short' }).format(
      new Date(iso),
    );
  const namen = useMemo(() => {
    const m = new Map<string, string>();
    for (const [id, u] of usersById) m.set(id, u.name);
    return m;
  }, [usersById]);

  const speichern = useMutation({
    mutationFn: () => api.updateStaffingComment(cm.staffing_comment_id, { body: text }),
    onSuccess: (row) => {
      setBearbeiten(false);
      onGespeichert(row);
    },
    onError: (err) =>
      toast.error(
        t('v2.staffing.comment_edit_failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });
  const loeschen = useMutation({
    mutationFn: () => api.deleteStaffingComment(cm.staffing_comment_id),
    onSuccess: onGeaendert,
    onError: (err) =>
      toast.error(
        t('v2.staffing.comment_delete_failed', 'Löschen fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  const fragLoeschen = () =>
    void bestaetigen({
      title: t('v2.staffing.comment_delete_title', 'Kommentar löschen?'),
      body: t('v2.staffing.comment_delete_body', 'Der Kommentar wird endgültig gelöscht.'),
      confirmLabel: t('v2.staffing.comment_delete', 'Löschen'),
      tone: 'danger',
    }).then((ok) => {
      if (ok) loeschen.mutate();
    });

  const knopf = { fontFamily: 'var(--f-mono)', color: 'var(--ink-3)', cursor: 'pointer' };
  const leer = !text.trim();
  const unveraendert = text === cm.body;

  return (
    <div
      className="rounded-md px-3 py-2"
      style={{ background: 'var(--surface-2)', color: 'var(--ink)' }}
    >
      <div
        className="flex items-center justify-between mb-1 text-[11px]"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        <span className="flex items-center gap-1.5" style={{ color: 'var(--ink-2)' }}>
          {author?.name ?? t('v2.detail.audit_author_unknown', 'unbekannt')}
          <HerkunftBadge projectId={cm.herkunft_project_id} projectKey={cm.herkunft_project_key} />
        </span>
        <span className="flex items-center gap-2">
          <span>{zeit(cm.created_at)}</span>
          {cm.edited_at && (
            <span title={zeit(cm.edited_at)}>
              {t('v2.staffing.comment_edited', 'bearbeitet {{zeit}}', { zeit: zeit(cm.edited_at) })}
            </span>
          )}
          {!bearbeiten && darfBearbeiten && (
            <button
              type="button"
              onClick={() => {
                setText(cm.body);
                setBearbeiten(true);
              }}
              style={knopf}
              aria-label={t('v2.staffing.comment_edit', 'Kommentar bearbeiten')}
              title={t('v2.staffing.comment_edit', 'Kommentar bearbeiten')}
            >
              {/* Eigenes SVG: das Zeichen ✎ rendert in der Mono-Schrift wie eine Spinne. */}
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                style={{ display: 'block' }}
              >
                <path d="M17 3a2.85 2.85 0 0 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
          )}
          {!bearbeiten && darfLoeschen && (
            <button
              type="button"
              onClick={fragLoeschen}
              disabled={loeschen.isPending}
              style={{ ...knopf, color: 'var(--danger)' }}
              aria-label={t('v2.staffing.comment_delete_aria', 'Kommentar löschen')}
              title={t('v2.staffing.comment_delete_aria', 'Kommentar löschen')}
            >
              ✕
            </button>
          )}
        </span>
      </div>
      {bearbeiten ? (
        <div className="flex flex-col gap-1">
          <MentionInput
            value={text}
            startWert={{ body: cm.body, namen }}
            onChange={(b: string) => setText(b)}
            rows={2}
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setBearbeiten(false)}
              className="text-xs px-3 py-1 rounded"
              style={{
                background: 'transparent',
                color: 'var(--ink-2)',
                border: '1px solid var(--line-soft)',
              }}
            >
              {t('v2.staffing.comment_cancel', 'Abbrechen')}
            </button>
            <button
              type="button"
              disabled={leer || unveraendert || speichern.isPending}
              onClick={() => speichern.mutate()}
              className="text-xs px-3 py-1 rounded"
              style={{
                background: 'var(--primary)',
                color: 'var(--on-primary)',
                border: 'none',
                opacity: leer || unveraendert || speichern.isPending ? 0.5 : 1,
                cursor: leer || unveraendert || speichern.isPending ? 'not-allowed' : 'pointer',
              }}
            >
              {t('v2.staffing.comment_save', 'Speichern')}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-sm whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>
          {renderMentionBody(cm.body, usersById as never)}
        </div>
      )}
    </div>
  );
}
